import { CancelInterest, Forwarder, FwPacket } from "@ndn/fw";
import { Data, Interest } from "@ndn/packet";
import { pushable } from "@ndn/util";
import { exactOptions } from "./common_browser.js";
import { makeRetxGenerator } from "./retx_browser.js";
export var ConsumerOptions;
(function (ConsumerOptions) {
    function exact(opts = {}) {
        return exactOptions(opts, ["modifyInterest", "retx", "verifier"]);
    }
    ConsumerOptions.exact = exact;
})(ConsumerOptions || (ConsumerOptions = {}));
function makeConsumer(interest, { fw = Forwarder.getDefault(), describe = `consume(${interest.name})`, signal, modifyInterest, retx, verifier, }) {
    Interest.makeModifyFunc(modifyInterest)(interest);
    let txTime = 0;
    let rtt;
    let nRetx = -1;
    const retxGen = makeRetxGenerator(retx)(interest.lifetime)[Symbol.iterator]();
    const promise = new Promise((resolve, reject) => {
        const rx = pushable();
        let timer;
        const cancelRetx = () => {
            clearTimeout(timer);
            timer = undefined;
        };
        const sendInterest = () => {
            cancelRetx();
            const { value, done } = retxGen.next();
            if (!done) {
                timer = setTimeout(sendInterest, value);
            }
            rx.push(FwPacket.create(interest));
            txTime = performance.now();
            ++nRetx;
        };
        const onAbort = () => {
            cancelRetx();
            rx.push(new CancelInterest(interest));
        };
        signal?.addEventListener("abort", onAbort);
        fw.addFace({
            rx,
            async tx(iterable) {
                for await (const pkt of iterable) {
                    if (pkt.l3 instanceof Data) {
                        rtt = performance.now() - txTime;
                        try {
                            await verifier?.verify(pkt.l3);
                        }
                        catch (err) {
                            reject(new Error(`Data verify failed: ${err} @${describe}`));
                            break;
                        }
                        resolve(pkt.l3);
                        break;
                    }
                    if (pkt.reject && !timer) {
                        reject(new Error(`Interest rejected: ${pkt.reject} @${describe}`));
                        break;
                    }
                }
                cancelRetx();
                signal?.removeEventListener("abort", onAbort);
                rx.stop();
            },
        }, {
            describe,
            local: true,
        });
        sendInterest();
    });
    return Object.defineProperties(promise, {
        interest: { value: interest },
        nRetx: { get() { return nRetx; } },
        rtt: { get() { return rtt; } },
    });
}
/**
 * Retrieve a single piece of Data.
 * @param interest - Interest or Interest name.
 */
export function consume(interest, opts = {}) {
    return makeConsumer(interest instanceof Interest ? interest : new Interest(interest), opts);
}
